/**
 * Who is reading, if anybody has to be.
 *
 * The server answers three states, not two: no password configured, password
 * configured and signed in, password configured and signed out. Collapsing the
 * first two would put a login form in front of a reader who never set one up.
 *
 * A fourth case has no server in it at all. Offline, this call is the first
 * thing the app does and the first thing that fails, and until it stopped
 * failing loudly, downloading a book for the train proved nothing: the reader
 * got "the server did not answer" and never reached the book sitting in the
 * cache underneath. So a network failure now falls back to the last answer the
 * server gave on this device.
 *
 * That is not a way past the password. What it restores is the state of the
 * door, never a credential: the cookie is still the only thing that opens a
 * request, every one of them still goes to the network, and the only content
 * readable in this state is what this same reader already downloaded here. A
 * device that has never been signed in has nothing remembered and still gets
 * the login screen.
 */

import { useCallback, useEffect, useState } from "react";

import { t } from "../i18n";
import { ApiError, api, type Session } from "../lib/api";

/** The last answer `/api/auth/me` gave on this device, for offline starts. */
const LAST_SESSION_KEY = "readcommons.session";

function remember(session: Session): void {
  try {
    localStorage.setItem(LAST_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private browsing. Offline starts will show the door instead; nothing else
    // breaks, so this is not worth an error the reader cannot act on.
  }
}

function recall(): Session | null {
  try {
    const raw = localStorage.getItem(LAST_SESSION_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const session = value as Partial<Session>;
    if (typeof session.required !== "boolean") return null;
    return { required: session.required, user: session.user ?? null } as Session;
  } catch {
    return null;
  }
}

export type SessionState =
  | { status: "loading" }
  | { status: "open" }
  | { status: "locked" }
  | { status: "in"; user: string }
  | { status: "down"; message: string };

export function useSession() {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  const settle = useCallback((session: Session) => {
    remember(session);
    if (!session.required) setState({ status: "open" });
    else if (session.user) setState({ status: "in", user: session.user });
    else setState({ status: "locked" });
  }, []);

  const refresh = useCallback(
    (signal?: AbortSignal) =>
      api
        .session(signal)
        .then(settle)
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === "AbortError") return;
          // An ApiError means the server did answer, and its answer stands.
          // Anything else means nothing answered at all, which is the offline
          // case: fall back to the last state this device was left in.
          if (!(error instanceof ApiError)) {
            const last = recall();
            if (last) {
              if (!last.required) setState({ status: "open" });
              else if (last.user) setState({ status: "in", user: last.user });
              else setState({ status: "locked" });
              return;
            }
          }
          const message =
            error instanceof ApiError ? error.message : t("errorNoServerDetail");
          setState({ status: "down", message });
        }),
    [settle],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);

  const signIn = useCallback(
    async (user: string, password: string) => {
      settle(await api.login(user, password));
    },
    [settle],
  );

  const signOut = useCallback(async () => {
    settle(await api.logout());
  }, [settle]);

  return { state, signIn, signOut, refresh };
}
